import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import {
  GeofenceCategory,
  GeofenceCategoryCreateInput,
  GeofenceCategoryUpdateInput,
} from '@models/geofence.model';
import OfficeIcon from '@mui/icons-material/Business';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ConstructionIcon from '@mui/icons-material/Construction';
import HomeIcon from '@mui/icons-material/Home';
import ApartmentIcon from '@mui/icons-material/Apartment';
import FactoryIcon from '@mui/icons-material/Factory';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StoreIcon from '@mui/icons-material/Store';
import GarageIcon from '@mui/icons-material/Garage';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ParkIcon from '@mui/icons-material/Park';
import CategoryIcon from '@mui/icons-material/Category';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaletteIcon from '@mui/icons-material/Palette';

interface GeofenceCategoryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: GeofenceCategoryCreateInput | GeofenceCategoryUpdateInput,
  ) => Promise<void>;
  category?: GeofenceCategory | null;
  isLoading?: boolean;
  globalCategories?: GeofenceCategory[];
}

interface IconOption {
  value: string;
  label: string;
  Icon: React.ElementType;
}

const ICON_OPTIONS: IconOption[] = [
  { value: 'office', label: 'Office', Icon: OfficeIcon },
  { value: 'warehouse', label: 'Warehouse', Icon: WarehouseIcon },
  { value: 'construction', label: 'Project', Icon: ConstructionIcon },
  { value: 'home', label: 'Residence', Icon: HomeIcon },
  { value: 'apartment', label: 'Apartment', Icon: ApartmentIcon },
  { value: 'factory', label: 'Factory', Icon: FactoryIcon },
  { value: 'location', label: 'Location', Icon: LocationOnIcon },
  { value: 'store', label: 'Store', Icon: StoreIcon },
  { value: 'garage', label: 'Yard', Icon: GarageIcon },
  { value: 'shipping', label: 'Shipping', Icon: LocalShippingIcon },
  { value: 'park', label: 'Park', Icon: ParkIcon },
  { value: 'general', label: 'General', Icon: CategoryIcon },
];

const COLOR_OPTIONS: { hex: string; label: string }[] = [
  { hex: '#3B82F6', label: 'Blue' },
  { hex: '#6366F1', label: 'Indigo' },
  { hex: '#8B5CF6', label: 'Purple' },
  { hex: '#EC4899', label: 'Pink' },
  { hex: '#EF4444', label: 'Red' },
  { hex: '#F59E0B', label: 'Amber' },
  { hex: '#EAB308', label: 'Yellow' },
  { hex: '#10B981', label: 'Emerald' },
  { hex: '#14B8A6', label: 'Teal' },
  { hex: '#06B6D4', label: 'Cyan' },
  { hex: '#64748B', label: 'Slate' },
  { hex: '#0F172A', label: 'Slate Dark' },
];

const DEFAULT_ICON = 'general';
const DEFAULT_COLOR = '#3B82F6';

export const GeofenceCategoryFormModal: React.FC<GeofenceCategoryFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  category,
  isLoading = false,
  globalCategories = [],
}) => {
  const isEditing = !!category;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState<string>(DEFAULT_ICON);
  const [color, setColor] = useState<string>(DEFAULT_COLOR);
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState('');
  const [nameError, setNameError] = useState('');

  const globalCategoryNames = useMemo(
    () => new Set(globalCategories.map((c) => c.name.trim().toLowerCase())),
    [globalCategories],
  );

  const validateName = (value: string): string => {
    const trimmed = value.trim().toLowerCase();
    if (trimmed && globalCategoryNames.has(trimmed)) {
      const matched = globalCategories.find(
        (c) => c.name.trim().toLowerCase() === trimmed,
      );
      return `"${matched?.name}" already exists as a global category. Use a different name.`;
    }
    return '';
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setNameError(validateName(value));
  };

  useEffect(() => {
    if (isOpen) {
      if (category) {
        setName(category.name || '');
        setDescription(category.description || '');
        setIcon(category.icon || DEFAULT_ICON);
        setColor(category.color || DEFAULT_COLOR);
        setIsActive(category.isActive ?? true);
        setSortOrder(category.sortOrder?.toString() || '');
      } else {
        setName('');
        setDescription('');
        setIcon(DEFAULT_ICON);
        setColor(DEFAULT_COLOR);
        setIsActive(true);
        setSortOrder('');
      }
      setNameError('');
    }
  }, [isOpen, category]);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    const error = validateName(name);
    if (error) {
      setNameError(error);
      return;
    }

    const baseData = {
      name: name.trim(),
      description: description.trim() || undefined,
      icon: icon || undefined,
      color: color || undefined,
      isActive,
      sortOrder: sortOrder ? parseInt(sortOrder, 10) : undefined,
    };

    if (isEditing && category) {
      await onSubmit({ geofenceCategoryId: category.geofenceCategoryId, ...baseData });
    } else {
      await onSubmit(baseData as GeofenceCategoryCreateInput);
    }
  };

  const SelectedIcon =
    ICON_OPTIONS.find((opt) => opt.value === icon)?.Icon ?? CategoryIcon;
  const selectedColorLabel =
    COLOR_OPTIONS.find((opt) => opt.hex === color)?.label ?? 'Custom';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Geofence Category' : 'Add Geofence Category'}
      confirmText={isEditing ? 'Save Changes' : 'Add Category'}
      handleConfirm={handleSubmit}
      isLoading={isLoading}
      disableConfirm={!name.trim() || !!nameError}
      size="medium"
    >
      <div className="flex flex-col gap-5">
        {/* Appearance */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <PaletteIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Appearance
          </h3>

          <div className="flex items-center gap-4 mb-4">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${color}20`, color }}
            >
              <SelectedIcon sx={{ fontSize: 28 }} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Preview</p>
              <p className="text-xs text-gray-500">
                {ICON_OPTIONS.find((o) => o.value === icon)?.label || 'General'} &middot;{' '}
                {selectedColorLabel}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
            <div className="grid grid-cols-6 gap-2">
              {ICON_OPTIONS.map(({ value, label, Icon }) => {
                const selected = icon === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setIcon(value)}
                    title={label}
                    className={`flex flex-col items-center justify-center p-2 rounded-lg border transition-all ${
                      selected
                        ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon
                      sx={{ fontSize: 22 }}
                      className={selected ? 'text-indigo-600' : 'text-gray-500'}
                    />
                    <span
                      className={`text-[10px] mt-1 truncate w-full text-center ${
                        selected ? 'text-indigo-600 font-medium' : 'text-gray-400'
                      }`}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((opt) => {
                const selected = color === opt.hex;
                return (
                  <button
                    key={opt.hex}
                    type="button"
                    onClick={() => setColor(opt.hex)}
                    title={opt.label}
                    style={{ backgroundColor: opt.hex }}
                    className={`w-8 h-8 rounded-full transition-all ${
                      selected
                        ? 'ring-2 ring-offset-2 ring-indigo-500 scale-110'
                        : 'hover:scale-105'
                    }`}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <InfoOutlinedIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Details
          </h3>
          <div className="flex flex-col gap-4">
            <FormGroup label="Category Name *" htmlFor="geofenceCategoryName">
              <Input
                id="geofenceCategoryName"
                value={name}
                onChange={handleNameChange}
                placeholder="e.g., Customer Site"
                autoFocus
              />
              {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
            </FormGroup>

            <FormGroup label="Description" htmlFor="geofenceCategoryDescription">
              <textarea
                id="geofenceCategoryDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this category..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                rows={2}
              />
            </FormGroup>

            <div className="grid grid-cols-2 gap-4">
              <FormGroup label="Sort Order" htmlFor="geofenceCategorySortOrder">
                <Input
                  id="geofenceCategorySortOrder"
                  type="number"
                  min="0"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  placeholder="0"
                />
                <p className="mt-1 text-xs text-gray-500">Lower numbers appear first</p>
              </FormGroup>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <div className="flex gap-4 mt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="geofenceCategoryStatus"
                      checked={isActive}
                      onChange={() => setIsActive(true)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="geofenceCategoryStatus"
                      checked={!isActive}
                      onChange={() => setIsActive(false)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { ICON_OPTIONS as GEOFENCE_CATEGORY_ICON_OPTIONS };

export default GeofenceCategoryFormModal;
